import { useState } from "react"
import { observeResize } from "../components/resizeObserver"
import { classes } from "./react"

type Props = React.PropsWithChildren<{

    title?: React.ReactNode
    icon?: string
    small: boolean
    className?: string
    /** Card footer, located inside the card and collapsed with the card. */
    footer?: React.ReactNode
    /** Card 'sticky footer', located outside of the card and does not collapse with the card - sticks to the card's bottom edge. */
    stickyFooter?: React.ReactNode

    headerTools?: React.ReactNode

    headerContent?: React.ReactNode
    headerContentAlign?: 'left' | 'right'

    /** If true, will track whether the vertical scrollbar is visible in the body, and set the .card-body-scrollable on the card when it is */
    trackScrollable?: boolean
    /** If true, the card will be initialized as collapsed. */
    collapsed?: boolean
    /** Enables the collapse/expand functionality. When true, a toggle icon (button) will be added to the tools in header. */
    isCollapsible?: boolean
    /** Callback to notify the parent when the collapse state changes. */
    onCollapseChange?: (newCollapsed: boolean) => void
}>

export function Card({ 
        title, 
        icon, 
        small, 
        children, 
        className, 
        footer, 
        stickyFooter,
        headerTools, 
        headerContent,
        headerContentAlign = 'left',
        collapsed = false,
        isCollapsible = false,
        onCollapseChange
    }: Props) {

    const [state, setState] = useState({ scrollbarVisible: false })
    const [isCollapsed, setIsCollapsed] = useState(collapsed);

    className = classes({ card: true, 'card-small': small, 'card-body-scrollable': state.scrollbarVisible, 'card-collapsed': isCollapsed, 'no-margin-bottom': !!stickyFooter }, className)

    const body = React.useRef<HTMLDivElement>()

    React.useEffect(() => {

        const onResize = () => {

            const elt = body.current
            const scroll = elt?.scrollHeight > elt?.clientHeight

            if (state.scrollbarVisible != scroll) {
                setState({ ...state, scrollbarVisible: scroll })
            }

        }

        if (body.current) {
            return observeResize(body.current, onResize, 0)
        }

    })

    const toggleCollapse = () => {
        if (!isCollapsible) {
            return;
        }

        const newCollapsed = !isCollapsed;
        setIsCollapsed(newCollapsed);

        if (onCollapseChange) {
            onCollapseChange(newCollapsed);
        }
    }

    return <>
    <div className={className} >

        <div className={`card-head content-align-${headerContentAlign}`}>
            {/* Icon and Title */}
            {
                (icon || title) &&
                <header>
                    {icon && <i className={icon}></i>}
                    {title}
                </header>
            }
            {/* Header Content */}
            {
                headerContent &&
                <div className="content">
                    {headerContent}
                </div>
            }
            {/* Tools */}
            {
                (headerTools || isCollapsible) &&
                <div className="tools">
                    {headerTools && headerTools}
                    {isCollapsible &&
                        <div className="btn-group">
                            <button className="btn btn-link btn-icon-toggle btn-collapse" onClick={toggleCollapse} title={isCollapsed ? "Extend" : "Minimize"}>
                                <i className="icon icon-down"></i>
                            </button>
                        </div>
                    }
                </div>
            }

        </div>

        <div className="card-body" ref={body}>
            {children}
        </div>

        {
            footer &&
            <div className="card-actionbar">
                <div className="card-actionbar-row">
                    {footer}
                </div>
            </div>
        }

    </div>
    {stickyFooter &&
        <div className="card-stickyfooter">
            {stickyFooter}
        </div>
    }
    </>
}

export function CardSmall(props: Omit<Props, "small">) {
    return <Card {...{ ...props, small: true }} />
}
